import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Skeleton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

/**
 * Check data for history display
 */
export interface CheckHistoryItem {
  id: string;
  status: 'UP' | 'DOWN';
  responseTime: number | null;
  statusCode: number | null;
  error: string | null;
  createdAt: Date | string;
}

/**
 * Pagination info
 */
export interface PaginationInfo {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * CheckHistory component props
 */
interface CheckHistoryProps {
  checks: CheckHistoryItem[];
  pagination?: PaginationInfo;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  isLoading?: boolean;
  title?: string;
  emptyMessage?: string;
}

/**
 * Format timestamp for display
 */
function formatTimestamp(date: Date | string): { date: string; time: string } {
  const d = new Date(date);
  return {
    date: d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    time: d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }),
  };
}

/**
 * Format relative time
 */
function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const checkDate = new Date(date);
  const diffMs = now.getTime() - checkDate.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays}d ago`;
}

/**
 * Format response time
 */
function formatResponseTime(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * Get status code color
 */
function getStatusCodeColor(statusCode: number | null): string {
  if (statusCode === null) return '#6B7280';
  if (statusCode >= 200 && statusCode < 300) return '#10B981';
  if (statusCode >= 300 && statusCode < 400) return '#3B82F6';
  if (statusCode >= 400 && statusCode < 500) return '#F59E0B';
  return '#EF4444';
}

/**
 * Status badge component
 */
function CheckStatusBadge({ status }: { status: 'UP' | 'DOWN' }) {
  const isUp = status === 'UP';
  return (
    <Chip
      size="small"
      icon={isUp ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <ErrorIcon sx={{ fontSize: 14 }} />}
      label={isUp ? 'Success' : 'Failed'}
      sx={{
        color: isUp ? '#10B981' : '#EF4444',
        backgroundColor: isUp ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 24,
        '& .MuiChip-icon': {
          color: isUp ? '#10B981' : '#EF4444',
        },
      }}
    />
  );
}

/**
 * Loading skeleton for the table
 */
function CheckHistorySkeleton({ rowCount = 5 }: { rowCount?: number }) {
  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Skeleton variant="text" width={150} height={28} />
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Skeleton variant="text" width={60} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={60} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={100} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={80} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={60} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: rowCount }).map((_, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Skeleton variant="text" width={80} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="rounded" width={70} height={24} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={60} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={40} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={100} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

/**
 * Mobile card view for a check
 */
function MobileCheckCard({ check }: { check: CheckHistoryItem }) {
  const { date, time } = formatTimestamp(check.createdAt);

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {date}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {time}
          </Typography>
        </Box>
        <CheckStatusBadge status={check.status} />
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mt: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Response Time
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {formatResponseTime(check.responseTime)}
          </Typography>
        </Box>

        {check.statusCode !== null && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Status Code
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: getStatusCodeColor(check.statusCode),
              }}
            >
              {check.statusCode}
            </Typography>
          </Box>
        )}
      </Box>

      {check.error && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Error
          </Typography>
          <Typography
            variant="body2"
            color="error"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {check.error}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * CheckHistory component
 * Displays a paginated table of check history
 */
export function CheckHistory({
  checks,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  isLoading = false,
  title = 'Check History',
  emptyMessage = 'No checks recorded yet',
}: CheckHistoryProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Handle page change
  const handlePageChange = (_event: unknown, newPage: number) => {
    onPageChange?.(newPage + 1); // Convert from 0-indexed to 1-indexed
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    onRowsPerPageChange?.(newRowsPerPage);
  };

  if (isLoading) {
    return <CheckHistorySkeleton />;
  }

  const hasChecks = checks.length > 0;

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {pagination && (
          <Typography variant="caption" color="text.secondary">
            {pagination.total} total checks
          </Typography>
        )}
      </Box>

      {/* Content */}
      {!hasChecks ? (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : isMobile ? (
        /* Mobile View */
        <Box>
          {checks.map((check) => (
            <MobileCheckCard key={check.id} check={check} />
          ))}
        </Box>
      ) : (
        /* Desktop Table View */
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Response Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status Code</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {checks.map((check) => {
                const { date, time } = formatTimestamp(check.createdAt);
                const relativeTime = formatRelativeTime(check.createdAt);

                return (
                  <TableRow
                    key={check.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      ...(check.status === 'DOWN' && {
                        backgroundColor: 'rgba(239, 68, 68, 0.04)',
                      }),
                    }}
                  >
                    <TableCell>
                      <Tooltip title={`${date} at ${time}`}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {relativeTime}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {time}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <CheckStatusBadge status={check.status} />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {formatResponseTime(check.responseTime)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {check.statusCode !== null ? (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: getStatusCodeColor(check.statusCode),
                          }}
                        >
                          {check.statusCode}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {check.error ? (
                        <Tooltip title={check.error}>
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{
                              maxWidth: 200,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {check.error}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {pagination && pagination.total > 0 && (
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1} // Convert from 1-indexed to 0-indexed
          rowsPerPage={pagination.pageSize}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        />
      )}
    </Paper>
  );
}

export default CheckHistory;
